import type { PlanNodeOut, SourceRefOut } from '@/client';
import type { MaintenanceProvider, MaintenanceSource } from '@/components/maintenance/model/cardViewModels';
import { CORE_SERVICE_CATEGORY_LABELS } from '@/utils/serviceCategoryUtils';

/**
 * Mappers from a plan node to the props the *designed* cards already take.
 *
 * This file used to hand-narrow four `{ [key: string]: unknown }` passthroughs. The backend has
 * since typed `SourceRefOut` and `ServiceContactOut`, and the invented rendering the readers fed is
 * gone — so what remains is the honest job: `SourceDocuments` and
 * `ProviderCard` are the design of record and are not edited to fit the API; the API is
 * mapped to them here.
 *
 * (`warranty.terms` is still a passthrough — sparse by design, a warranty states only the clauses
 * it has — but nothing renders it, so nothing guards it. If a card for it is ever designed, its
 * reader returns here.)
 */

/**
 * Source references the current user may see.
 *
 * The maintenance plan is not yet visibility-aware, while the Records list is, so the set the caller
 * passes is the authorization boundary: a reference absent from the authorized Records response is
 * not named, paged or linked. It is the loading state too — an empty set while that query is in
 * flight hides sources rather than flashing a filename the reader may not be entitled to.
 *
 * TODO(schedule-source-visibility): remove this client-side join once the plan endpoint filters or
 * redacts provenance for the requesting user. See docs/schedule/source-visibility.md.
 */
export const visiblePlanSourceRefs = (node: PlanNodeOut, visibleDocumentIds: ReadonlySet<number>): SourceRefOut[] =>
	(node.provenance?.source_refs ?? []).filter((ref) => visibleDocumentIds.has(ref.doc_id));

/**
 * What a surface sees before it has been given a visibility set.
 *
 * Named rather than inlined so that omitting the set is a deliberate "show nothing" rather than an
 * accidental "show everything" — the mistake this whole join exists to prevent. The review cards
 * take the set as a required prop instead; docs/schedule/source-visibility.md records which
 * components are on which contract, and why both exist for now.
 */
export const NO_VISIBLE_DOCUMENTS: ReadonlySet<number> = new Set();

/**
 * The node's sources, in the shape the designed provenance rows take.
 *
 * Two gaps are carried honestly rather than papered over: the plan names documents by numeric
 * `doc_id` only — the `public_id` ask was declined — so linking needs the caller's resolver, built
 * from the house documents list; and the plan sends `pages: []` where the design shows one page, so
 * the first contributing page is named.
 *
 * Visibility and linkability are separate questions and must stay so. `visibleDocumentIds` decides
 * whether the reader may see a source at all; the resolver decides only whether its row can be
 * clicked. A document the Records list returned but carried no `public_id` for is therefore named
 * and inert — answering both questions with the resolver drops a row the reader is entitled to.
 */
export const planSourcesForCard = (
	node: PlanNodeOut,
	visibleDocumentIds: ReadonlySet<number>,
	resolveDocumentPublicId?: (documentId: number) => string | undefined,
): MaintenanceSource[] =>
	visiblePlanSourceRefs(node, visibleDocumentIds).map((ref) => ({
		documentId: ref.doc_id,
		documentPublicId: resolveDocumentPublicId?.(ref.doc_id) ?? '',
		filename: ref.filename,
		page: ref.pages?.[0],
	}));

/**
 * How the registry's relationship token reads under a contact's name.
 *
 * `role` is `provider_to_system.role`: either one of the registry tokens the client knows how to
 * name, or free text already written for a reader. The API explicitly says an unrecognised value
 * reads as "Contact", so an unknown machine token is not dressed up as plausible product copy.
 *
 * TODO(api-contact-category): the plan's `ServiceContactOut` carries neither `category_code` nor
 * `scope`, though `ContactCandidateOut` and `PropertyContactWithDetails` both do. So the drawer is
 * the one surface that cannot name a contact's trade the way the directory does — it reads
 * `scope || tradeLabel` off the category system, and this stands in for that. A client-side join
 * is not the answer: both contacts reads are permission-gated, so the reader who gets the plainest
 * card would get no label at all. When the plan carries a category, draw that and delete this.
 */
const PROVIDER_ROLE_LABELS: Readonly<Record<string, string>> = {
	...CORE_SERVICE_CATEGORY_LABELS,
	manufacturer: 'Manufacturer',
	installer: 'Installer',
	servicer: 'Servicer',
};

export const providerRoleLabel = (role: string | null | undefined): string | undefined => {
	const trimmed = role?.trim();
	if (!trimmed) return undefined;

	const knownLabel = PROVIDER_ROLE_LABELS[trimmed.toLowerCase()];
	if (knownLabel) return knownLabel;

	// Spaces or capitals distinguish reader-written free text from the snake-case registry tokens
	// that require an explicit mapping above.
	if (!trimmed.includes('_') && (/\s/.test(trimmed) || /[A-Z]/.test(trimmed))) {
		return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
	}

	return 'Contact';
};

/**
 * The service contact the designed "Who can help" card names.
 *
 * The card was designed for a single provider, so where a system carries several, the reconciler's
 * primary is the one named and payload order only settles what it cannot: the same rule the
 * directory sorts its Key contacts by. A contact without a name is nothing to show, and cannot win
 * the choice by being primary either.
 *
 * TODO(api-contact-overrides): `ServiceContactOut` documents name, phone and email as registry
 * values, while Network edits write property-specific overrides. This card makes phone and email
 * actionable, so the plan endpoint must eventually return the resolved values; another client
 * invalidation cannot make registry-only fields reflect a household's correction.
 */
export const planProviderForCard = (node: PlanNodeOut): MaintenanceProvider | undefined => {
	const named = node.system?.service_contacts?.filter((entry) => entry.name) ?? [];
	const contact = named.find((entry) => entry.tier === 'primary') ?? named[0];
	return contact?.name
		? {
				name: contact.name,
				role: providerRoleLabel(contact.role),
				phone: contact.phone ?? undefined,
				email: contact.email ?? undefined,
				status: contact.status ?? undefined,
				candidateId: contact.candidate_id ?? undefined,
				contactId: contact.property_contact_id ?? undefined,
			}
		: undefined;
};
